import React from 'react';
import { motion } from 'framer-motion';
import { Lock, Sparkles, Calendar, ShieldCheck } from 'lucide-react';

const pillars = [
  {
    icon: Lock,
    title: 'Licensed & Fully Insured',
    body: 'Peace of mind with every visit. Workers compensation and liability coverage protect every property we step into.',
  },
  {
    icon: Sparkles,
    title: 'White-Glove Detail',
    body: 'We clean what others overlook. Baseboards, light switches, behind appliances — the small things define the experience.',
  },
  {
    icon: Calendar,
    title: 'Flexible Scheduling',
    body: 'Recurring, one-time, or emergency cleans. Same-day turnover available for Airbnb hosts across the DMV.',
  },
  {
    icon: ShieldCheck,
    title: '48-Hour Re-Clean Guarantee',
    body: "If something isn't right, we come back within 48 hours and re-clean it. No questions, no charge.",
  },
];

export const TrustBadges = () => {
  return (
    <section
      className="relative bg-white py-20 md:py-28"
      data-testid="trust-section"
    >
      <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
        {/* Editorial heading */}
        <div className="text-center mb-16 md:mb-20">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex items-center justify-center gap-3 mb-5"
          >
            <div className="h-px w-10 bg-[#C9A84C]" />
            <span className="text-[#C9A84C] text-[11px] tracking-[0.45em] uppercase font-semibold">
              Why Tsalach
            </span>
            <div className="h-px w-10 bg-[#C9A84C]" />
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="font-serif text-[#1A202C] leading-[1.05] tracking-tight max-w-3xl mx-auto"
            style={{ fontSize: 'clamp(1.85rem, 4.5vw, 3.25rem)' }}
          >
            The standard isn't average.
            <br />
            <span className="italic font-light">It's exceptional.</span>
          </motion.h2>
        </div>

        {/* 4 pillar cards (2x2 mobile, 4 columns lg) */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {pillars.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className="group relative bg-white border border-[#0D1B2A]/10 hover:border-[#C9A84C]/50 p-7 md:p-8 transition-all duration-500 hover:shadow-[0_20px_40px_-20px_rgba(13,27,42,0.18)] hover:-translate-y-1"
                data-testid={`pillar-${i}`}
              >
                {/* Gold top accent */}
                <div className="absolute top-0 left-0 right-0 h-[2px] bg-[#C9A84C] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />

                <Icon
                  className="w-8 h-8 text-[#C9A84C] mb-6"
                  strokeWidth={1.5}
                />
                <h3 className="font-serif text-xl md:text-2xl text-[#1A202C] mb-3 leading-tight">
                  {p.title}
                </h3>
                <p className="text-sm md:text-[15px] text-[#4A5568] leading-relaxed">
                  {p.body}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
